import { Recurrence } from '@tableau/self-service-schedules';
import React from 'react';

import { assertUnreachable } from '~commonInfra/utils/TypescriptHelpers';
import { SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { FlowOutputSelectorAction } from '~src/flow/components/FlowOutputSelectorContent';
import { FlowScheduleSelectorAction } from '~src/flow/components/FlowScheduleSelector';
import { DEFAULT_SHOULD_ATTACH_OUTPUT_DATA_TO_EMAIL,DEFAULT_SHOULD_INCLUDE_LINK_TO_DATASOURCE, DEFAULT_SHOULD_SHOW_FIRST_N_ROWS, isSelectedOutputAPublishedDatasource } from '~src/flow/utils/FlowSubscriptionUtil';

import { FlowLinkedTaskOptionsAction } from '../components/flows/FlowLinkedTaskOptions';
import { LinkedTaskFlowSelectorAction } from '../components/LinkedTaskItemSelectorContent';
import { LinkedTaskAddItemBeforeAfterAction } from '../components/LinkedTaskMoveItemButton';
import { LinkedTaskToggleExpandAction } from '../components/LinkedTasksDialogContent';
import { displayMaximumLinkedTaskMessage,insertInArray,LinkedTaskFlowInitialState, linkedTaskInitialState, maximumLinkedTasks,updateInArray } from '../utils/LinkedTaskHelpers';
import { LinkedTaskDialogAddActions, LinkedTaskDialogState, LinkedTaskFlowState, LinkedTaskItemStateType, LinkedTaskOutputSelectionState, LinkedTaskPositionActions } from './LinkedTaskStateTypes';

export type LinkedTaskDialogAction =
  FlowScheduleSelectorAction |
  LinkedTaskDialogItemStateAction |
  { type: 'setScheduleFrequency', value: Recurrence } |
  { type: 'setState', value: LinkedTaskDialogState };

type LinkedTaskDialogItemStateAction =
  LinkedTaskToggleExpandAction |
  FlowLinkedTaskOptionsAction |
  LinkedTaskFlowSelectorAction |
  LinkedTaskDialogAddActions |
  LinkedTaskPositionActions |
  FlowOutputSelectorAction |
  LinkedTaskAddItemBeforeAfterAction;

function itemsReducer(items: LinkedTaskItemStateType[], action: LinkedTaskDialogItemStateAction): LinkedTaskItemStateType[] {
  let flow: LinkedTaskFlowState;
  let updatedItem;
  switch(action.type) {
    // Adding flows
    case 'addFlow':
      if (items.length >= maximumLinkedTasks) {
        displayMaximumLinkedTaskMessage();
        return items;
      }
      items.splice(action.payload.position !== undefined ? action.payload.position : items.length, 0, action.payload.value);
      return [...items];
    case 'addFlows':
      if (items.length + action.payload.value.length >= maximumLinkedTasks) {
        displayMaximumLinkedTaskMessage();
        return items;
      }
      items.splice(action.payload.position !== undefined ? action.payload.position : items.length, action.payload.overwrite ? 1 : 0, ...action.payload.value);
      return [...items];
    // Adding a new blank item
    case 'addNextTask':
      if (items.length >= maximumLinkedTasks) {
        displayMaximumLinkedTaskMessage();
        return items;
      }
      return [...items, {}];
    // Moving items
    case 'moveItem':
      const item = items.splice(action.payload.index, 1)[0];
      switch(action.payload.position) {
        case 'up':
          const index = action.payload.index > 0 ? action.payload.index - 1 : 0;
          items.splice(index, 0, item);
          break;
        case 'down':
          items.splice(action.payload.index + 1, 0, item);
          break;
        case 'beginning':
          items.unshift(item);
          break;
        case 'end':
          items.push(item);
          break;
      }
      return [...items];
    case 'addBeforeAfter':
      if (items.length >= maximumLinkedTasks) {
        displayMaximumLinkedTaskMessage();
        return items;
      }
      return insertInArray(action.payload.index, action.payload.position, {}, items);
    // Flow Options
    case 'setDqwEnabled':
      updatedItem =  {...items[action.payload.index],
        dqwEnabled: action.payload.value
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setStopRemainingTasks':
      updatedItem = {...items[action.payload.index],
        stopRemainingTasks: action.payload.value
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setShouldSendEmailWhenDone':
      updatedItem =  {...items[action.payload.index],
        shouldSendEmailWhenDone: action.payload.value
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'deleteTaskEntry':
      items.splice(action.value, 1);
      return [...items];
    // Flow Subscription
    case 'toggleShouldSendToMe':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        flowSubscriptions: {...flow.flowSubscriptions,
          shouldSendToMe: !flow.flowSubscriptions.shouldSendToMe
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'toggleShouldIncludeOutputData':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        flowSubscriptions: {...flow.flowSubscriptions,
          shouldIncludeOutputData: !flow.flowSubscriptions.shouldIncludeOutputData
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'toggleAttachOutputDataToEmail':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        flowSubscriptions: {...flow.flowSubscriptions,
          shouldAttachOutputDataToEmail: !flow.flowSubscriptions.shouldAttachOutputDataToEmail
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'toggleShowFirstNRows':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        flowSubscriptions: {...flow.flowSubscriptions,
          shouldShowFirstNRows: !flow.flowSubscriptions.shouldShowFirstNRows
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'toggleIncludeLinkToDatasource':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        flowSubscriptions: {...flow.flowSubscriptions,
          shouldIncludeLinkToDatasource: !flow.flowSubscriptions.shouldIncludeLinkToDatasource
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'changeFlowSubscriptionSubject':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        flowSubscriptions: {...flow.flowSubscriptions,
          subjectValue: action.payload.value
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'changeFlowSubscriptionMessage':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        flowSubscriptions: {...flow.flowSubscriptions,
          messageValue: action.payload.value
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'changeRecipients':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        flowSubscriptions: {...flow.flowSubscriptions,
          emailRecipients: action.payload.value
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'changeFileFormat':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        flowSubscriptions: {...flow.flowSubscriptions,
          outputFileFormat: action.payload.value
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'changeSelectedOutputForSubscription':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      const isOnPremEnvironment: boolean = !SessionServerSelectors.isOnlineEnvironment(getAppStore().getState());
      const isSelectedOutputPublishedDatasource: boolean = isSelectedOutputAPublishedDatasource(action.payload.value, action.payload.outputs);
      if (isOnPremEnvironment) {
        if (isSelectedOutputPublishedDatasource) {
          updatedItem = {...flow,
            flowSubscriptions: {...flow.flowSubscriptions,
              selectedOutputForSubscription: action.payload.value,
              shouldAttachOutputDataToEmail: false,
              shouldIncludeLinkToDatasource: DEFAULT_SHOULD_INCLUDE_LINK_TO_DATASOURCE,
              shouldShowFirstNRows: DEFAULT_SHOULD_SHOW_FIRST_N_ROWS
            }
          };
        } else { // output that is not a published datasource
          updatedItem = {...flow,
            flowSubscriptions: {...flow.flowSubscriptions,
              selectedOutputForSubscription: action.payload.value,
              shouldAttachOutputDataToEmail: DEFAULT_SHOULD_ATTACH_OUTPUT_DATA_TO_EMAIL,
              shouldIncludeLinkToDatasource: false,
              shouldShowFirstNRows: DEFAULT_SHOULD_SHOW_FIRST_N_ROWS
            }
          };
        }
      } else { // online
        updatedItem = {...flow,
          flowSubscriptions: {...flow.flowSubscriptions,
            selectedOutputForSubscription: action.payload.value,
            shouldIncludeLinkToDatasource: DEFAULT_SHOULD_INCLUDE_LINK_TO_DATASOURCE
          }
        };
      }
      return updateInArray(action.payload.index, updatedItem, items);
    // DQW Warning
    case 'setEnableTrigger':
      // This is the same action as setDqwEnabled
      updatedItem =  {...items[action.payload.index],
        dqwEnabled: action.payload.value
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setIsSevere':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        dqw: {...flow.dqw,
          isSevere: action.payload.value
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setMessage':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        dqw: {...flow.dqw,
          message: action.payload.value
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setFlow':
      updatedItem = LinkedTaskFlowInitialState(action.payload.flowId, action.payload.flowName, (items[action.payload.index] as LinkedTaskFlowState).linkedTaskStepId);
      return updateInArray(action.payload.index, updatedItem, items);
    case 'toggleExpandItem':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        isExpanded: !flow.isExpanded
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setParameterOverrides':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        parameterOverrides: action.payload.value
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setRequiredParameters':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        requiredParameters: action.payload.value
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'selectOutput':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      if (flow.outputSelection.selectedOutputs[action.payload.value] && action.payload.value === flow.flowSubscriptions.selectedOutputForSubscription) {
        // then its going to be unselected so update flow subscription as appropriate, and still update the outputSelection
        updatedItem = {
          ...flow,
          outputSelection: outputSelectionReducer(flow.outputSelection, action),
          flowSubscriptions: {...flow.flowSubscriptions,
            selectedOutputForSubscription: null,
            shouldIncludeOutputData: false,
            shouldIncludeLinkToDatasource: false,
          }
        };
        return updateInArray(action.payload.index, updatedItem, items);
      }
      // fall through to default to allow outputSelectionReducer to do its job like normal
    case 'selectOutputs':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      const payloadSelection = action.payload.value as { readonly [key: string]: boolean };
      if (!payloadSelection[flow.flowSubscriptions.selectedOutputForSubscription]) {
        // then its going to be unselected so update flow subscription as appropriate, and still update the outputSelection
        updatedItem = {
          ...flow,
          outputSelection: outputSelectionReducer(flow.outputSelection, action),
          flowSubscriptions: {...flow.flowSubscriptions,
            selectedOutputForSubscription: null,
            shouldIncludeOutputData: false,
            shouldIncludeLinkToDatasource: false,
          }
        };
        return updateInArray(action.payload.index, updatedItem, items);
      }
      // fall through to default to allow outputSelectionReducer to do its job like normal
    default:
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {
        ...flow,
        outputSelection: outputSelectionReducer(flow.outputSelection, action),
      };
      return updateInArray(action.payload.index, updatedItem, items);
  }
}

function outputSelectionReducer(state: LinkedTaskOutputSelectionState, action: FlowOutputSelectorAction): LinkedTaskOutputSelectionState {
  switch(action.type) {
    case 'selectOutput':
      return {...state,
        selectedOutputs: {
          ...state.selectedOutputs,
          [action.payload.value]: state.selectedOutputs[action.payload.value] ? !state.selectedOutputs[action.payload.value] : true,
        }
      };
    case 'selectOutputs':
      return {...state,
        selectedOutputs: action.payload.value,
      };
    case 'setIsIncremental':
      return {...state,
        isIncrementalMap: {
          ...state.isIncrementalMap,
          [action.payload.outputId]: action.payload.value,
        }
      };
    case 'setFutureOutputsIncluded':
      return {...state,
        futureOutputsIncluded: action.payload.value,
      };
    case 'setAllOutputsIncremental':
      return {...state,
        runAllOption: action.payload.value,
      };
    case 'setSelectAllChecked':
      return  {...state,
        selectAllChecked: action.payload.value,
      };
    default:
      assertUnreachable(action);
      return state;
  }
}

function reducer(state: LinkedTaskDialogState, action: LinkedTaskDialogAction): LinkedTaskDialogState {
  switch (action.type) {
    // Setting full state (editing existing linked task)
    case 'setState':
      return action.value;
    // Schedule Selector
    case 'setSelectedSchedule':
      return {
        ...state,
        selectedSchedule: action.value
      };
    case 'setScheduleFrequency':
      return {
        ...state,
        scheduledTaskFrequency: action.value
      };
    default:
      return {
        ...state,
        items: itemsReducer(state.items, action)
      };
  }
}

export function useLinkedTaskDialogStateHook(): [LinkedTaskDialogState, React.Dispatch<LinkedTaskDialogAction>] {
  const [state, dispatch] = React.useReducer(reducer, linkedTaskInitialState);
  return [state, dispatch];
}
